import { CORE_PHASE_ORDER, CORE_PHASE_RANK } from './corePhases';
import { isNegativeBoardSquare } from './doorBoard';

export const CORRUPTION_PENALTY = 100;
export const CORE_PHASE_SPELL_MS = 2100;

export const getCoreSpellProgress = (spell, now = Date.now()) =>
  Math.max(0, Math.min(1, (now - spell.startedAt) / spell.duration));

export const getCoreSpellRevealAt = (spell, index) => {
  const row = spell.spells.length === 1 ? spell.spells[0].row : null;
  const position =
    row !== null
      ? (index % 5) / 4
      : Math.max(
          0,
          spell.changes.findIndex((change) => change.index === index)
        ) / Math.max(1, spell.changes.length - 1);
  return 0.4 + 0.45 * position;
};

// A spell takes away an advantage. Existing hazards and neutral tiles are
// never targets, including mixed tiles with an otherwise helpful effect.
export const isBeneficialPlayerTile = (square) =>
  !!square &&
  !square.coreDamage &&
  !isNegativeBoardSquare(square) &&
  (square.modifier > 0 ||
    (square.operation === 'multiply' && square.value > 1) ||
    [
      'critical',
      'heal',
      'shield',
      'relic',
      'shards',
      'relay',
      'gambit',
      'powerUp',
      'interference',
      'sabotage',
      'recharge',
    ].includes(square.effect?.type));

const pick = (values, random) =>
  values[Math.min(values.length - 1, Math.floor(random() * values.length))];

// Core power is a landing effect, not a hidden increase to the damage die.
// Leave misses, negative modifiers and Sabotage's changes available as escapes.
export const strengthenCoreBoard = (board, phase) => {
  const level = CORE_PHASE_RANK[phase] || 0;
  if (!level || !Array.isArray(board)) return board;
  const next = board.map((square) =>
    square.effect?.type === 'corePower'
      ? { ...square, effect: { type: 'corePower', level } }
      : square
  );
  const replaceHelpfulTile = (effect) => {
    const index = next.findIndex(
      (square) => !square.effect && square.modifier > 0
    );
    if (index >= 0)
      next[index] = {
        modifier: effect.type === 'corePower' ? next[index].modifier : 0,
        coreDamage: true,
        effect,
      };
    return index >= 0;
  };
  let powerCount = next.filter((s) => s.effect?.type === 'corePower').length;
  while (powerCount < 2 && replaceHelpfulTile({ type: 'corePower', level }))
    powerCount += 1;
  const targetMassive = 2 + Math.max(0, level - 1);
  let massiveCount = next.filter(
    (s) => s.effect?.type === 'massiveAttack'
  ).length;
  while (
    massiveCount < targetMassive &&
    replaceHelpfulTile({ type: 'massiveAttack' })
  )
    massiveCount += 1;
  return next;
};

export const applyCoreBoardPhase = (game, phase, random = Math.random) => {
  const target = CORE_PHASE_RANK[phase] || 0;
  const applied = CORE_PHASE_RANK[game.coreBoardPhase] || 0;
  if (
    target <= applied ||
    game.bossHp <= 0 ||
    !Array.isArray(game.doorBoard) ||
    game.doorBoard.length !== 25
  )
    return game;
  const board = [...game.doorBoard];
  const rows = [...(game.coreCorruptedRows || [])];
  const changes = [];
  const spells = [];
  const corrupt = (index, sourcePhase) => {
    const before = board[index];
    const after = {
      modifier: -CORRUPTION_PENALTY,
      effect: { type: 'corruption' },
      corruptedBy: sourcePhase,
    };
    changes.push({ index, before, after });
    board[index] = after;
  };
  const eligible = () =>
    board.flatMap((square, index) =>
      isBeneficialPlayerTile(square) ? [index] : []
    );
  for (let rank = applied + 1; rank <= target; rank += 1) {
    const sourcePhase = CORE_PHASE_ORDER[rank];
    let row = null;
    if (['emergence', 'evolution'].includes(sourcePhase)) {
      const candidates = [
        ...new Set(eligible().map((i) => Math.floor(i / 5))),
      ].filter((candidate) => !rows.includes(candidate));
      if (candidates.length) {
        row = pick(candidates, random);
        rows.push(row);
        eligible()
          .filter((i) => Math.floor(i / 5) === row)
          .forEach((index) => corrupt(index, sourcePhase));
      }
    } else {
      // Fracture cracks two advantages; Meltdown spreads to two more. The two
      // row spells remain the main hazards, with room left to aim around them.
      for (let count = 0; count < 2; count += 1) {
        const candidates = eligible();
        if (candidates.length) corrupt(pick(candidates, random), sourcePhase);
      }
    }
    spells.push({ phase: sourcePhase, row });
  }
  // A previously settled Core die must keep its exact landing calculation.
  const bossBoard = Number.isInteger(game.bossLandingIndex)
    ? game.bossDoorBoard
    : strengthenCoreBoard(game.bossDoorBoard, phase);
  const bossChanges = (bossBoard || []).flatMap((after, index) =>
    JSON.stringify(after) === JSON.stringify(game.bossDoorBoard[index])
      ? []
      : [{ index, before: game.bossDoorBoard[index], after }]
  );
  return {
    ...game,
    coreBoardPhase: phase,
    coreCorruptedRows: rows,
    doorBoard: board,
    bossDoorBoard: bossBoard,
    corePhaseSpell: {
      id: `${game.bossRound}:${phase}`,
      phase,
      beforePhase: CORE_PHASE_ORDER[applied],
      changes,
      bossChanges,
      spells,
    },
  };
};

export const completeCorePhaseSpell = (game, id) =>
  game.corePhaseSpell?.id === id ? { ...game, corePhaseSpell: null } : game;

// Grandfather older fights at their current phase. Their next transformation
// introduces the new rules without rewriting a saved landing or snapshot.
export const migrateCoreBoardPhases = (game) =>
  game.phase?.startsWith('boss') && !game.coreBoardPhase
    ? {
        ...game,
        coreBoardPhase: game.corePhase,
        coreCorruptedRows: [],
        corePhaseSpell: null,
      }
    : game;
